import React, { useState, useEffect } from 'react';
import API from '../api';
import SEO from "../components/SEO";

const DonatePage = () => {
    const [programmes, setProgrammes] = useState([]);
    const [form, setForm] = useState({ programme: '', donorName: '', email: '' });
    const [status, setStatus] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredProgrammes, setFilteredProgrammes] = useState([]);

    useEffect(() => {
        // Fetch courses from API
        const fetchCourses = async () => {
            try {
                const response = await API.get('/courses');
                setProgrammes(response.data || []);
                setFilteredProgrammes(response.data || []);
            } catch (err) {
                console.error('Failed to fetch courses:', err);
            }
        };
        fetchCourses();
    }, []);

    useEffect(() => {
        // Filter programmes based on search term
        const filtered = programmes.filter(p => 
            p.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredProgrammes(filtered);
    }, [searchTerm, programmes]);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setShowDropdown(true);
    };

    const handleSelectProgramme = (title) => {
        setForm((f) => ({ ...f, programme: title }));
        setSearchTerm(title);
        setShowDropdown(false);
    };

    const handleInputFocus = () => {
        setShowDropdown(true);
    };

    const handleInputBlur = () => {
        // Delay to allow click on dropdown item
        setTimeout(() => setShowDropdown(false), 200);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('processing');
        const payload = { ...form };

        try {
            if (API && API.post) {
                await API.post('/donations', payload);
            } else {
                console.log('Donation payload', payload);
            }
            setStatus('success');
        } catch (err) {
            console.error(err);
            setStatus('error');
        }
    };

    const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/donate';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': 'Donate - Africa Research University',
        'description': 'Support scholarships and programmes at Africa Research University.',
        'url': pageUrl
    };

    return (
        <section className="min-h-screen py-16 bg-gray-50">
            <SEO title="Donate — Africa Research University" description="Support scholarships and programmes at Africa Research University." image="/assets/img/arulogo.png" url={pageUrl} jsonLd={jsonLd} />
            <div className="max-w-3xl my-16 mx-auto px-6">
                <div className="flex items-center mb-6">
                    <a href="/alumni?tab=get-involved" className="flex items-center text-blue-600 hover:text-blue-800">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Alumni
                    </a>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-4">Request to Donate to a Programme</h1>
                <p className="text-gray-600 mb-6">Choose a programme you'd like to support. We'll contact you with information on how to complete your donation.</p>

                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow">
                    <div className="grid grid-cols-1 gap-4">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                placeholder="Search and select a programme"
                                className="w-full p-3 border rounded"
                                required={!form.programme}
                            />
                            {showDropdown && filteredProgrammes.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {filteredProgrammes.map((p) => (
                                        <div
                                            key={p.id}
                                            onClick={() => handleSelectProgramme(p.title)}
                                            className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                                        >
                                            {p.title}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {showDropdown && searchTerm && filteredProgrammes.length === 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-gray-500">
                                    No programmes found
                                </div>
                            )}
                        </div>
                        <input name="donorName" value={form.donorName} onChange={handleChange} placeholder="Your name" className="p-3 border rounded" />
                        <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email" className="p-3 border rounded" />

                        <div className="flex items-center gap-3 mt-2">
                            <button type="submit" className="px-5 py-2 bg-green-600 text-white rounded">Submit Request</button>
                            {status === 'processing' && <span className="text-sm">Submitting your request...</span>}
                            {status === 'success' && <span className="text-sm text-green-600">Thank you! Someone will contact you shortly about how to make your donation.</span>}
                            {status === 'error' && <span className="text-sm text-red-600">Failed to submit request</span>}
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
};

export default DonatePage;
